import { IonFooter, SegmentValue } from '@ionic/react';
import { useEffect, useRef, useState } from 'react';

import ClockingSummary from '../../components/clockingSummary/ClockingSummary';
import JobSearchContent from '../../components/jobSearch/jobSearchContent/JobSearchContent';
import JobSearchHeader from '../../components/jobSearch/jobSearchHeader/JobSearchHeader';

import { useHistory } from 'react-router';
import { logTransaction } from '../../common/log';
import { getQueryString } from '../../common/util';
import ClockingHistoryList from '../../components/clockingHistoryList/ClockingHistoryList';
import DashboardFooter, { DashboardSelectedTab } from '../../components/dashboardFooter/DashboardFooter';
import { IRepairOrder } from '../../models/dms/repairOrder.model';
import { LogType } from '../../models/log.model';
import { RouteNames } from '../../models/routes.model';
import { useToast } from '../../providers/ToastProvider';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setSelectedRepairOrderStoreAsync } from '../../store/repairOrderSlice';
import { IonPageWrapper } from './DashboardPage.styles';

interface IComponentProps {}

const DashboardPage: React.FC<IComponentProps> = () => {
  const [selectedTab, setSelectedTab] = useState(DashboardSelectedTab.ClockingSummary);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  const { isDemo } = useAppSelector((state) => state.app);

  const jobSearchHeaderRef = useRef<React.ElementRef<typeof JobSearchHeader>>(null);

  const {
    actions: { errorToast },
  } = useToast();
  const history = useHistory();
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Handle cases where the component loads for the first time
    const tabValue = getQueryString('tab') || DashboardSelectedTab.ClockingSummary;
    compareTabsAndRedirect(tabValue || DashboardSelectedTab.ClockingSummary);

    // Handle cases where the URL changes outside of this component
    const historyListener = history.listen((url) => {
      if (url.pathname === RouteNames.dashboard || url.pathname === RouteNames.demo) {
        const tabValue = url.search.split('=')[1];
        const newTab = DashboardSelectedTab[tabValue as keyof typeof DashboardSelectedTab];
        setSelectedTab(newTab);
      }
    });

    setHasSearched(false);

    return () => historyListener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle cases where the tabs are clicked
  const handleChangeTab = (tabValue: SegmentValue | undefined) => {
    setSearchKeyword('');
    compareTabsAndRedirect(tabValue || DashboardSelectedTab.ClockingSummary);
  };

  const compareTabsAndRedirect = (tabValue: SegmentValue) => {
    const newTab = DashboardSelectedTab[tabValue as keyof typeof DashboardSelectedTab];
    if (selectedTab !== newTab) {
      setSelectedTab(newTab);

      if (isDemo) {
        history.replace({
          pathname: RouteNames.demo,
          search: `tab=${tabValue}`,
        });
      } else {
        history.replace({
          pathname: RouteNames.dashboard,
          search: `tab=${tabValue}`,
        });
      }
    }
  };

  const handleClickRepairOrder = async (e: React.MouseEvent, repairOrder: IRepairOrder) => {
    logTransaction(LogType.Info, `Selected repair order '${repairOrder.repairOrderNumber}'`);
    e.preventDefault();

    if (!repairOrder.isClosed && repairOrder.isConfirmed && !repairOrder.isInvoiced && !repairOrder.isQuotation && repairOrder.isVetted) {
      await dispatch(setSelectedRepairOrderStoreAsync(repairOrder));
      history.push({
        pathname: RouteNames.jobList,
        search: `repairOrderNumber=${repairOrder.repairOrderNumber}`,
      });
    } else {
      errorToast('Repair order is not available');
    }
  };

  return (
    <IonPageWrapper>
      {(() => {
        switch (selectedTab) {
          case DashboardSelectedTab.ClockingSummary:
            return <ClockingSummary />;
          case DashboardSelectedTab.JobSearch:
            return (
              <>
                <JobSearchHeader
                  ref={jobSearchHeaderRef}
                  handleSearch={(val) => {
                    setHasSearched(true);
                    setSearchKeyword(val);
                  }}
                />
                <JobSearchContent handleClickRepairOrder={(e, ro) => handleClickRepairOrder(e, ro)} handleFilterChanged={(extended) => jobSearchHeaderRef.current?.applyFilter(extended)} hasSearched={hasSearched} searchKeyword={searchKeyword} />
              </>
            );
          case DashboardSelectedTab.ClockingHistory:
            return <ClockingHistoryList />;
          default:
            return <ClockingSummary />;
        }
      })()}

      <IonFooter>
        <DashboardFooter selectedTab={selectedTab} handleChangeTab={(tab) => handleChangeTab(tab)} />
      </IonFooter>
    </IonPageWrapper>
  );
};

export default DashboardPage;
